import React, { useState, useRef } from "react";
import { Icon } from "antd";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Slider from "react-slick";
import placeholder from "../../../../../images/bg.jpg";
import { SeparateNumberFormat, hasAccessRole, isCpmiCompany } from "../../../../../services/utils";
import { isDynamicContent } from "../../../../../services/contents";
import Colors from "../../../../../themes/Colors";
import UserRoleEnum from "../../../../../constants/UserRoleEnum";
import { formatMoney } from "../../utils/campaignDetailData";
import ChangeAdButtonContainer from "../../containers/ChangeAdButtonContainer";
import {
  getRealShows,
  getRevisionTag,
  getCreativeThumb,
  getVideoSource,
  isVideoContent,
  getAdAspect,
  getAdFormatLabel,
  isSubsidized,
  formatVersion,
  parseRules,
} from "../../utils/screenAdsData";
import RulesPanel from "./RulesPanel";

const CreativePreview = ({ relation }) => {
  const [playing, setPlaying] = useState(false);
  const thumb = getCreativeThumb(relation);
  const isVideo = isVideoContent(relation);
  const isDynamic = isDynamicContent(relation.content_type);
  // A dynamic ad's preview is any shape, so it is shown whole rather than cropped
  // to the slot (the shared class covers by default).
  const fitStyle = isDynamic ? { objectFit: "contain" } : undefined;

  if (isVideo && playing) {
    return (
      <video className="screenAd__creative-img" style={fitStyle} controls autoPlay>
        <source src={getVideoSource(relation)} type="video/mp4" />
      </video>
    );
  }

  // Same fallback as the library and the settings step: a dynamic ad with no
  // usable preview (none uploaded, or its transcoding failed) gets the globe.
  if (!thumb) {
    return (
      <div
        className="screenAd__creative-empty"
        style={
          isDynamic
            ? { display: "flex", alignItems: "center", justifyContent: "center" }
            : undefined
        }
      >
        {isDynamic && (
          <Icon type="global" style={{ fontSize: 32, color: Colors.primaryAccent }} />
        )}
      </div>
    );
  }

  return (
    <>
      <img className="screenAd__creative-img" style={fitStyle} src={thumb} alt="" />
      {isVideo && (
        <button
          type="button"
          className="screenAd__play"
          onClick={() => setPlaying(true)}
        >
          <Icon type="caret-right" />
        </button>
      )}
    </>
  );
};

const MiniCard = ({ icon, label, value }) => (
  <div className="screenAd__mini">
    <div className="screenAd__mini-top">
      <span className="screenAd__mini-icon">
        <Icon type={icon} />
      </span>
    </div>
    <span className="screenAd__mini-label">{label}</span>
    <span className="screenAd__mini-value">{value}</span>
  </div>
);

const AdSlide = ({
  relation,
  campaign,
  reportSingleData,
  loadingSingleReport,
  t,
  hideControls,
  canChangeAd,
  hideRealSpots,
  hidePurchasedSpots
}) => {
  const realShows = getRealShows(relation, reportSingleData);
  const rules = parseRules(relation);
  const versionLabel = formatVersion(relation.content_version_name, t);
  const aspect = getAdAspect(relation);
  const sideLayout =
    aspect.orientation === "vertical" || aspect.orientation === "square";
  const wrapStyle = sideLayout
    ? { aspectRatio: `${aspect.w} / ${aspect.h}` }
    : { paddingTop: `${(aspect.h / aspect.w) * 100}%` };

  // real playouts only exist once the owner approved the content
  const showRealSpots = !hideRealSpots && relation.revision_status === "approved";
  // cpmi hides playouts cards; the grid adapts to how many are left
  const miniCount = 4 - (showRealSpots ? 0 : 1) - (hidePurchasedSpots ? 1 : 0);

  const showChangeAd =
    canChangeAd &&
    (!relation.content_child_contents ||
      relation.content_child_contents.length === 0);

  return (
    <div className="screenAd__slide">
      <div className="screenAd__center">
        {!hideControls && (versionLabel || showChangeAd) && (
          <div className="screenAd__creative-top">
            {versionLabel ? (
              <span className="screenAd__version">{versionLabel}</span>
            ) : (
              <span />
            )}
            {showChangeAd && (
              <ChangeAdButtonContainer campaign={campaign} relation={relation} />
            )}
          </div>
        )}

        <div
          className={`screenAd__media screenAd__media--${aspect.orientation}${
            sideLayout ? " screenAd__media--side" : ""
          }`}
        >
          <div className="screenAd__creative-wrap" style={wrapStyle}>
            <CreativePreview relation={relation} />
          </div>

          <div className={`screenAd__minis screenAd__minis--${miniCount}`}>
          <MiniCard
            icon="dollar"
            label={t("Price")}
            value={
              <>
                {formatMoney(relation.paid_amount_with_profit, campaign.currency)}
                {isSubsidized(relation) && <span className="screenAd__subsidized">{t("Subsidized")}</span>}
              </>
            }
          />
          {showRealSpots && (
            <MiniCard
              icon="eye"
              label={t("Real spots")}
              value={loadingSingleReport ? <Icon type="loading" /> : realShows != null ? SeparateNumberFormat(realShows) : "—"}
            />
          )}
          {!hidePurchasedSpots && (
            <MiniCard
              icon="play-square"
              label={relation.belong_to_smart_campaign ? t("Purchased spots") : t("Promised impressions")}
              value={SeparateNumberFormat(relation.promised_shows)}
            />
          )}
          <MiniCard
            icon="fullscreen"
            label={t("Ad format")}
            value={getAdFormatLabel(relation, t)}
          />
          </div>
        </div>
      </div>

      <div className="screenAd__rules">
        <RulesPanel rules={rules} />
      </div>
    </div>
  );
};

const ScreenAdItem = ({ group, campaign, reportSingleData, loadingSingleReport, mode, user }) => {
  const { t } = useTranslation();
  const { screen, ads } = group;
  const isProposal = mode === "proposal";
  // cpmi companies only see executed playouts, and proposals have none yet
  const isCpmi = isCpmiCompany(user);
  const playoutsVisibility = { hidePurchasedSpots: isCpmi, hideRealSpots: isCpmi && isProposal };
  const [adIndex, setAdIndex] = useState(0);
  const sliderRef = useRef(null);

  const current = ads[Math.min(adIndex, ads.length - 1)];
  const tag = getRevisionTag(current, t);

  const canChangeAd =
    !isProposal &&
    user &&
    hasAccessRole(user.roles, [
      UserRoleEnum.agencyMember,
      UserRoleEnum.companyMember,
    ]);

  const sliderSettings = {
    dots: false,
    arrows: false,
    infinite: false,
    speed: 400,
    slidesToShow: 1,
    slidesToScroll: 1,
    adaptiveHeight: true,
    afterChange: (i) => setAdIndex(i),
  };

  return (
    <div className="screenAd">
      <div className="screenAd__screen">
        <div className="screenAd__title">{screen.display_name}</div>
        <img className="screenAd__screen-img" src={screen.display_picture || placeholder} alt="" />
        {tag && (
          <span className="screenAd__status-tag" style={{ borderColor: tag.color, color: tag.color }}>
            {tag.label}
          </span>
        )}
        {screen.display_use_cms === 0 && (
          <span className="screenAd__cms-tag">{t("The owner of this screen does not use cms")}</span>
        )}
        <div className="screenAd__format">
          {t("Screen Format")}: <b>{screen.display_resolution_width}x{screen.display_resolution_height}</b>
        </div>
        <Link to={"/screen/" + screen.display_id} className="screenAd__detail-btn">
          {t("See detail")}
        </Link>
      </div>

      <div className="screenAd__body">
        {!isProposal && ads.length > 1 ? (
          <Slider ref={sliderRef} {...sliderSettings}>
            {ads.map((relation, i) => (
              <div key={`${relation.id}-${i}`}>
                <AdSlide
                  relation={relation}
                  campaign={campaign}
                  reportSingleData={reportSingleData}
                  loadingSingleReport={loadingSingleReport}
                  t={t}
                  canChangeAd={canChangeAd}
                  {...playoutsVisibility}
                />
              </div>
            ))}
          </Slider>
        ) : (
          <AdSlide
            relation={ads[0]}
            campaign={campaign}
            reportSingleData={reportSingleData}
            loadingSingleReport={loadingSingleReport}
            t={t}
            hideControls={isProposal}
            canChangeAd={canChangeAd}
            {...playoutsVisibility}
          />
        )}

        {!isProposal && ads.length > 1 && (
          <div className="screenAd__ad-pager">
            <button
              type="button"
              className="screenAd__arrow"
              onClick={() => sliderRef.current && sliderRef.current.slickPrev()}
            >
              <Icon type="left" />
            </button>
            <span className="screenAd__ad-pager-label">
              {t("Ad")} {adIndex + 1} {t("of")} {ads.length}
            </span>
            <button
              type="button"
              className="screenAd__arrow"
              onClick={() => sliderRef.current && sliderRef.current.slickNext()}
            >
              <Icon type="right" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScreenAdItem;
